import { useState, useEffect, useRef } from "react";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";
import { Mail } from "lucide-react";

interface EmailAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  organizationId?: string;
}

interface EmailSuggestion {
  email: string;
  displayName?: string;
}

export const EmailAutocomplete = ({
  value,
  onChange,
  placeholder = "Empfänger",
  className,
  organizationId
}: EmailAutocompleteProps) => {
  const [suggestions, setSuggestions] = useState<EmailSuggestion[]>([]);
  const [allEmails, setAllEmails] = useState<EmailSuggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Load all known email addresses on mount
  useEffect(() => {
    const loadKnownEmails = async () => {
      if (!organizationId) return;

      try {
        // Fetch unique email addresses from sent and received emails
        const { data: emails, error } = await supabase
          .from('emails')
          .select('from_address, to_addresses')
          .eq('organization_id', organizationId)
          .order('created_at', { ascending: false })
          .limit(500);

        if (error) {
          console.error('Error loading emails for autocomplete:', error);
          return;
        }

        const emailSet = new Map<string, EmailSuggestion>();

        emails?.forEach(email => {
          // Add from_address
          if (email.from_address && !email.from_address.includes('noreply')) {
            const fromEmail = extractEmail(email.from_address);
            const fromName = extractName(email.from_address);
            if (fromEmail && !emailSet.has(fromEmail.toLowerCase())) {
              emailSet.set(fromEmail.toLowerCase(), {
                email: fromEmail,
                displayName: fromName
              });
            }
          }

          // Add to_addresses
          const toAddresses = email.to_addresses as string[];
          toAddresses?.forEach(addr => {
            const toEmail = extractEmail(addr);
            const toName = extractName(addr);
            if (toEmail && !emailSet.has(toEmail.toLowerCase())) {
              emailSet.set(toEmail.toLowerCase(), {
                email: toEmail,
                displayName: toName
              });
            }
          });
        });

        // Also load customer emails
        const { data: customers } = await supabase
          .from('customers')
          .select('email, first_name, last_name')
          .not('email', 'is', null);

        customers?.forEach(customer => {
          if (customer.email && !emailSet.has(customer.email.toLowerCase())) {
            emailSet.set(customer.email.toLowerCase(), {
              email: customer.email,
              displayName: [customer.first_name, customer.last_name].filter(Boolean).join(' ') || undefined
            });
          }
        });

        setAllEmails(Array.from(emailSet.values()));
      } catch (err) {
        console.error('Error in loadKnownEmails:', err);
      }
    };

    loadKnownEmails();
  }, [organizationId]);

  // Extract email from "Name <email@example.com>" format
  const extractEmail = (str: string): string | null => {
    const match = str.match(/<([^>]+)>/);
    if (match) return match[1];
    if (str.includes('@')) return str.trim();
    return null;
  };

  // Extract name from "Name <email@example.com>" format
  const extractName = (str: string): string | undefined => {
    const match = str.match(/^([^<]+)</);
    if (match) return match[1].trim();
    return undefined;
  };

  // Filter suggestions based on input
  useEffect(() => {
    const currentInput = value.split(',').pop()?.trim().toLowerCase() || '';
    
    if (currentInput.length < 1) {
      setSuggestions([]);
      return;
    }

    const filtered = allEmails.filter(item => 
      item.email.toLowerCase().includes(currentInput) ||
      (item.displayName && item.displayName.toLowerCase().includes(currentInput))
    ).slice(0, 8);

    setSuggestions(filtered);
    setSelectedIndex(-1);
  }, [value, allEmails]);

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showSuggestions || suggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => Math.min(prev + 1, suggestions.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => Math.max(prev - 1, -1));
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0) {
          selectSuggestion(suggestions[selectedIndex]);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        break;
      case 'Tab':
        if (selectedIndex >= 0) {
          e.preventDefault();
          selectSuggestion(suggestions[selectedIndex]);
        }
        break;
    }
  };

  const selectSuggestion = (suggestion: EmailSuggestion) => {
    const parts = value.split(',');
    parts.pop(); // Remove current input
    const newValue = [...parts.map(p => p.trim()), suggestion.email].filter(Boolean).join(', ');
    onChange(newValue);
    setShowSuggestions(false);
    setSelectedIndex(-1);
    inputRef.current?.focus();
  };

  return (
    <div ref={containerRef} className="relative flex-1">
      <Input
        ref={inputRef}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setShowSuggestions(true)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        className={className}
        autoComplete="off"
      />
      
      {showSuggestions && suggestions.length > 0 && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-popover border border-border rounded-md shadow-lg z-50 max-h-48 overflow-y-auto">
          {suggestions.map((suggestion, index) => (
            <button
              key={suggestion.email}
              type="button"
              onClick={() => selectSuggestion(suggestion)}
              className={cn(
                "w-full px-3 py-2 text-left text-sm flex items-center gap-2 hover:bg-accent transition-colors",
                selectedIndex === index && "bg-accent"
              )}
            >
              <Mail className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
              <div className="flex flex-col min-w-0">
                {suggestion.displayName && (
                  <span className="text-foreground truncate">{suggestion.displayName}</span>
                )}
                <span className={cn(
                  "truncate",
                  suggestion.displayName ? "text-muted-foreground text-xs" : "text-foreground"
                )}>
                  {suggestion.email}
                </span>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
